import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, LogOut, Sparkles } from 'lucide-react';
import { useAuth } from '../App';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useTheme } from '../design/ThemeContext';

const API = `${window.location.origin}/api`;

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, logout, checkAuth } = useAuth();
  const { refresh } = useWorkspace();
  const { toggleTheme } = useTheme();
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user?.name) {
      setName(`${user.name} Workspace`);
    }
  }, [user?.name]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error('Workspace ismi gerekli');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/onboarding`, { workspaceName: trimmed });
      const wsId = res.data?.workspace?.id;

      if (wsId) {
        localStorage.setItem('onx_active_workspace', wsId);
      }

      // Service worker, /api/auth/me ve /api/workspaces/me yanıtlarını cache'leyebiliyor.
      // Workspace yeni yaratıldıktan sonra stale yanıt OnboardingGate'i tetiklediği için
      // bu yolları SW cache'inden temizliyoruz.
      try {
        if ('caches' in window) {
          const keys = await caches.keys();
          for (const key of keys) {
            const cache = await caches.open(key);
            const reqs = await cache.keys();
            await Promise.all(
              reqs
                .filter((r) => /\/api\/(auth\/me|workspaces\/me)/.test(r.url))
                .map((r) => cache.delete(r))
            );
          }
        }
      } catch (_) { /* sessizce yut, kritik değil */ }

      await checkAuth();
      await refresh();
      toast.success('Workspace oluşturuldu');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Workspace oluşturulamadı';
      toast.error(msg);
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg)',
      color: 'var(--text)',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: "'Inter Tight',system-ui,sans-serif",
    }}>
      <header style={{
        padding: '18px 28px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid var(--border)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img src="/onxflow_logo.png" alt="OnxFlow" style={{ width: 36, height: 36, borderRadius: 8 }} />
          <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-.02em' }}>OnxFlow</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            onClick={toggleTheme}
            title="Tema"
            style={{
              width: 34, height: 34,
              border: '1px solid var(--border)', borderRadius: 8,
              background: 'var(--surface)', color: 'var(--text-muted)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 14.5A8 8 0 019.5 4a8 8 0 1010.5 10.5z" />
            </svg>
          </button>
          <button
            type="button"
            onClick={logout}
            style={{
              padding: '7px 12px',
              border: '1px solid var(--border)', borderRadius: 8,
              background: 'var(--surface)', color: 'var(--text-muted)',
              fontSize: 12.5,
              display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
            }}
          >
            <LogOut size={13} />
            <span>Çıkış</span>
          </button>
        </div>
      </header>

      <main style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 32,
      }}>
        <div style={{
          width: '100%',
          maxWidth: 460,
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 14,
          padding: 32,
          boxShadow: '0 1px 3px rgba(0,0,0,.04)',
        }}>
          <div style={{
            width: 48, height: 48,
            borderRadius: 12,
            background: 'var(--accent-weak, rgba(126,182,240,.15))',
            color: 'var(--accent)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: 18,
          }}>
            <Sparkles size={22} />
          </div>

          <h1 style={{
            margin: 0,
            fontSize: 26, fontWeight: 600, letterSpacing: '-.022em', lineHeight: 1.15,
          }}>
            Hoş geldin{user?.name ? `, ${user.name}` : ''}!
          </h1>
          <p style={{
            marginTop: 8, marginBottom: 24,
            fontSize: 13.5, color: 'var(--text-muted)', lineHeight: 1.55,
          }}>
            Başlamak için workspace'ine bir isim ver. Free plan ile 1 koltuk ve 1 proje hakkın var, daha sonra dilediğin zaman büyütebilirsin.
          </p>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={{ display: 'block', fontSize: 11.5, color: 'var(--text-muted)', fontWeight: 500, marginBottom: 6 }}>
                Workspace ismi
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Örn: Acme Workspace"
                maxLength={80}
                required
                autoFocus
                data-testid="onboarding-workspace-name"
                style={{
                  width: '100%',
                  padding: '11px 12px',
                  fontSize: 14,
                  background: 'var(--surface-muted)',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  color: 'var(--text)',
                  outline: 'none',
                }}
              />
              <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 4 }}>
                Daha sonra workspace ayarlarından değiştirilebilir.
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting || !name.trim()}
              data-testid="onboarding-submit"
              style={{
                width: '100%',
                padding: '12px 14px',
                background: 'var(--accent)',
                color: '#fff',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 600,
                border: '1px solid var(--accent)',
                cursor: submitting || !name.trim() ? 'not-allowed' : 'pointer',
                opacity: submitting || !name.trim() ? .65 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : null}
              <span>{submitting ? 'Workspace oluşturuluyor…' : 'Workspace\'i oluştur ve başla'}</span>
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}
